import { clsx, type ClassValue } from 'clsx'
import { customAlphabet } from 'nanoid'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const nanoid = customAlphabet(
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  7
) // 7-character random string

/**
 * Gets a display name for a user, falling back to email prefix if name is not available
 * @param user - User object with name and email properties
 * @returns Display name string
 */
export function getUserDisplayName(user: { name?: string | null; email: string }): string {
  // If name exists and is not empty, use it
  if (user.name && user.name.trim().length > 0) {
    return user.name.trim()
  }
  
  // Fall back to email prefix (part before @)
  const emailPrefix = user.email.split('@')[0]
  
  // Format email prefix: remove dots/underscores and capitalize
  return emailPrefix
    .replace(/[._-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Safely converts a date to ISO string format
 * Handles both Date objects and string dates from APIs
 * @param date - Date object or ISO string
 * @returns ISO string representation of the date
 */
export function toISOString(date: Date | string): string {
  if (typeof date === 'string') {
    // If it's already an ISO string, return as is, otherwise convert
    return date.includes('T') ? date : new Date(date).toISOString()
  }
  return date.toISOString()
}

export async function fetcher<JSON = any>(
  input: RequestInfo,
  init?: RequestInit
): Promise<JSON> {
  const res = await fetch(input, init)

  if (!res.ok) {
    const json = await res.json()
    if (json.error) {
      const error = new Error(json.error) as Error & {
        status: number
      }
      error.status = res.status
      throw error
    } else {
      throw new Error('An unexpected error occurred')
    }
  }

  return res.json()
}

export function formatDate(input: string | number | Date): string {
  const date = new Date(input)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

export const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)

export const runAsyncFnWithoutBlocking = (
  fn: (...args: any) => Promise<any>
) => {
  fn()
}

export const sleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms))

export const getStringFromBuffer = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

export enum ResultCode {
  InvalidCredentials = 'INVALID_CREDENTIALS',
  InvalidSubmission = 'INVALID_SUBMISSION',
  InvalidReferralCode = 'INVALID_REFERRAL_CODE',
  UserAlreadyExists = 'USER_ALREADY_EXISTS',
  UnknownError = 'UNKNOWN_ERROR',
  UserCreated = 'USER_CREATED',
  UserLoggedIn = 'USER_LOGGED_IN'
}

export const getMessageFromCode = (resultCode: string) => {
  switch (resultCode) {
    case ResultCode.InvalidCredentials:
      return 'Invalid credentials!'
    case ResultCode.InvalidReferralCode:
      return 'Invalid referral code! For now, only invitees can sign up.'
    case ResultCode.InvalidSubmission:
      return 'Invalid submission, please try again!'
    case ResultCode.UserAlreadyExists:
      return 'User already exists, please log in!'
    case ResultCode.UserCreated:
      return 'User created, welcome!'
    case ResultCode.UnknownError:
      return 'Something went wrong, please try again!'
    case ResultCode.UserLoggedIn:
      return 'Logged in!'
  }
}

export function format(date: Date, formatString: string) {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ]

  return formatString
    .replace('yyyy', year.toString())
    .replace('yy', String(year).slice(-2))
    .replace('LLL', monthNames[month])
    .replace('MM', String(month + 1).padStart(2, '0'))
    .replace('dd', String(day).padStart(2, '0'))
    .replace('d', day.toString())
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

export function parseISO(dateString: string) {
  return new Date(dateString)
}

export function subMonths(date: Date, amount: number) {
  const newDate: Date = new Date(date)
  newDate.setMonth(newDate.getMonth() - amount)
  return newDate
}

/**
 * Filters updates to only include fields that have actually changed
 * @param original - The original object
 * @param updates - The proposed updates
 * @param excludeFields - Fields to exclude from comparison (e.g., 'id', 'createdAt', 'updatedAt')
 * @returns Object containing only the fields that have actually changed
 */
export function getChangedFields<T extends Record<string, any>>(
  original: T,
  updates: Partial<T>,
  excludeFields: string[] = ['id', 'createdAt', 'updatedAt']
): Partial<T> {
  const changedFields: Partial<T> = {}

  Object.keys(updates).forEach(key => {
    if (excludeFields.includes(key)) {
      return // Skip excluded fields
    }

    const originalValue = original[key]
    const newValue = updates[key]

    // Deep comparison for objects and arrays, shallow for primitives
    const hasChanged = JSON.stringify(originalValue) !== JSON.stringify(newValue)

    if (hasChanged) {
      changedFields[key as keyof T] = newValue
    }
  })

  return changedFields
}

/**
 * Gets a human-readable list of changed field names
 * @param original - The original object
 * @param updates - The proposed updates
 * @param excludeFields - Fields to exclude from comparison
 * @returns Array of field names that have changed
 */
export function getChangedFieldNames<T extends Record<string, any>>(
  original: T,
  updates: Partial<T>,
  excludeFields: string[] = ['id', 'createdAt', 'updatedAt']
): string[] {
  const changedFields = getChangedFields(original, updates, excludeFields)
  return Object.keys(changedFields)
}
